import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_state_ref } from '@/hooks/_base/use_state_ref';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { use_slice_collections, use_slice_generation, use_slice_ui, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { use_grid2d_slug_offset_set_to_url } from '@/pages/_base/grid2d/_hooks/use_grid2d_slug_offset_set_to_url';
import { HOOK_GRID2D_CELL__get_url_relative } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__get_url_relative';
import { HOOK__image_drag } from '@/pages/page_generation/_hooks/HOOK__image_drag';
import { G_WRAPPER_IMAGE__preview_on_layout_output } from '@/pages/page_generation/_watchers/G_WRAPPER_IMAGE__preview_on_layout_output';
import { LABEL__image_info } from '@/pages/page_generation/layouts/nodes/node/fields/image/_base/LABEL__image_info';
import { REMOTE__collection_image_add } from '@/state/collection/cell/REMOTE__collection_image_add';
import { create_new_cell_for_collection } from '@/utils/utils_collections';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { DTO_REQUEST__collection_image_add } from '@shared/dto/collection/_requests/DTO_REQUEST__collection_image_add';
import { ENUM__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { ENUM__position_9 } from '@shared/enums/ENUM__position_9';
import React, { useMemo, useState } from 'react';
import { CONTEXT_MENU__image } from '../../image/CONTEXT_MENU__image';
import { HOOK_GRID2D_CELL__image_select } from '../_hooks/HOOK_GRID2D_CELL__image_select';
import { HOOK_GRID2D_CELL__image_select_by_double_click } from '../_hooks/HOOK_GRID2D_CELL__image_select_by_double_click';
import { PROPS__grid2d_cell } from './GRID2D_CELL__image_content_or_empty';
import { GRID2D_CELL__loading_image } from './GRID2D_CELL__loading_image';
import { GRID2D_CELL__placeholder } from './GRID2D_CELL__placeholder';
import { GRID2D_CELL_BORDER__image_select } from './GRID2D_CELL_BORDER__image_select';
import { GRID2D_CELL_OVERLAY__buttons } from './GRID2D_CELL_OVERLAY__buttons';

export const GRID2D_CELL__image_content = React.memo(
  ({
    entity,
    grid2d,
    cell,
    can_show_buttons = true,
    className = '',

    height,
    width,
    //
    use_offset_from_slug,
  }: PROPS__grid2d_cell) => {
    /*
     * INPUTS
     */

    let { image } = cell;

    /*
     * STORE
     */

    const dispatch = use_dispatch();

    const { workspace_current, current_workspace_user } = use_slice_workspace(['workspace_current', 'current_workspace_user']);
    const { is_authorized, user_settings } = use_slice_user(['is_authorized', 'user_settings']);
    const { ui_grid2d_cells_sort_newest_first } = user_settings;
    const { generation_current } = use_slice_generation(['generation_current']);
    const { global__is_shift_pressed } = use_slice_ui(['global__is_shift_pressed']);

    const { collection_viewed_on_search, special_collections_info_current } = use_slice_collections([
      'collection_viewed_on_search',
      'special_collections_info_current',
    ]);

    /*
     * STATE
     */
    const set_param_grid2d_offset_to_url = use_grid2d_slug_offset_set_to_url();

    const ref_image = use_ref_with_fallback(ENUM__html_ref_stub.IMAGE);

    const [is_image_loaded, set_is_image_loaded, ref_is_image_loaded] = use_state_ref(false);

    const [is_mouse_over, set_is_mouse_over] = useState(false);

    const is_cell_size_ok_for_buttons = useMemo(
      () => get_is_cell_size_ok_for_buttons(),
      [generation_current.tool.graph, is_mouse_over, user_settings.ui_grid2d_cell_size],
    );

    const { on_user_uses_shortcut, on_user_uses_component } = use_hint_shortcut(EShortcutType.GRID2D_CELL_OPEN_FULLSCREEN_IMAGE);

    const url_image_relative = HOOK_GRID2D_CELL__get_url_relative(cell);

    const { on_click__image_select } = HOOK_GRID2D_CELL__image_select(cell);

    const { onDragStart, onDragEnd } = HOOK__image_drag(cell.image);

    let { image_select_by_double_click } = HOOK_GRID2D_CELL__image_select_by_double_click(cell.image);

    /*
     * EFFECTS
     */

    use_effect(() => {
      if (ref_is_image_loaded.current) return;

      // Create a new IntersectionObserver to watch when the image enters the viewport
      let observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          // If the entry is not intersecting (not visible), skip it
          if (!entry.isIntersecting) continue;

          let tag_image = entry.target as HTMLImageElement;

          if (!tag_image.dataset.src) continue;

          tag_image.src = tag_image.dataset.src;

          set_is_image_loaded(true);

          // Stop observing this image because it has been loaded
          observer.unobserve(tag_image);

          break;
        }
      });

      // Start observing the image referenced by 'ref_image'
      observer.observe(ref_image.current);

      // Cleanup: when the component unmounts or the dependency changes, stop observing
      return () => {
        observer.unobserve(ref_image.current);
        ref_is_image_loaded.current = false;
      };
    }, [url_image_relative]);

    /*
     * EVENTS
     */

    async function onDoubleClick(e: React.MouseEvent<HTMLImageElement, MouseEvent>) {
      e.stopPropagation();

      image_select_by_double_click();
    }

    async function onClick() {
      const grid2d_target_cell_index = grid2d
        .cells__get_list(ui_grid2d_cells_sort_newest_first)
        .findIndex(item => item.hash_sha256_content == cell.hash_sha256_content);

      use_offset_from_slug && set_param_grid2d_offset_to_url(grid2d_target_cell_index);

      on_click__image_select();
    }

    async function onMouseEnter() {
      set_is_mouse_over(true);

      await try_add_to_collection_viewed();
    }

    /*
     * METHODS
     */

    function get_is_cell_size_ok_for_buttons() {
      if (generation_current.is_stub()) return false;
      if (user_settings.ui_grid2d_cell_size == ENUM__grid2d_cell_size.XS || user_settings.ui_grid2d_cell_size == ENUM__grid2d_cell_size.S) return false;

      return true;
    }

    async function try_add_to_collection_viewed() {
      //todo:review and implement
      return;
      if (!is_authorized) return; //guest user
      if (collection_viewed_on_search.is_stub()) return; //collection not loaded yet or user doesnt have permissions to add cells to it
      if (cell.workspace_id == workspace_current._id) return; //created on this workspace
      if (cell.user_id_creator == current_workspace_user.user_id) return; //created by this user
      if (special_collections_info_current.images_ids_viewed_on_search.has(cell.hash_sha256_content)) return; //already viewed

      let cell_new = create_new_cell_for_collection(collection_viewed_on_search, image);

      let request = new DTO_REQUEST__collection_image_add(workspace_current._id, [cell_new]);

      await dispatch(REMOTE__collection_image_add(request));
    }

    /*
     * RENDERS
     */

    const rendered_buttons_frame = useMemo(() => {
      return render_buttons();
    }, [is_cell_size_ok_for_buttons, cell, entity, is_mouse_over, global__is_shift_pressed, can_show_buttons]);

    function render_buttons() {
      if (!can_show_buttons) return null;
      if (!is_mouse_over) return null;
      if (!is_cell_size_ok_for_buttons) return null;

      return <GRID2D_CELL_OVERLAY__buttons {...{ entity, cell }} />;
    }

    function render_content() {
      if (url_image_relative.is_blank()) return <GRID2D_CELL__loading_image {...{ width, height }} />;

      //
      return (
        <CONTEXT_MENU__image image_asset={image}>
          <div className={`size-full ${class_name_animation} `}>
            <img
              ref={ref_image}
              data-src={path_relative_to_absolute(url_image_relative)}
              className={`object-contain ${class_name_animation} 
              ${className} 
              ${is_image_loaded ? 'opacity-100 ' : 'opacity-0'}
              `}
              alt=""
              draggable={true}
              style={{ width, height, willChange: 'transform', backgroundColor: 'transparent', display: 'block' }}
              {...{ onClick }}
            />
          </div>
        </CONTEXT_MENU__image>
      );
    }

    function render_labels() {
      if (is_mouse_over) return null;
      if (image.is_stub()) return null;
      if (!is_cell_size_ok_for_buttons) return null;
      if (!user_settings.ui_grid2d__show_image_info) return null;

      return (
        <div className="absolute inset-0 pointer-events-none">
          <LABEL__image_info text={image.image_size.toString()} position={ENUM__position_9.TOP_LEFT} />
        </div>
      );
    }

    function render_content_inside_wrapper(content: React.ReactNode) {
      if (entity.type == ENUM__workspace_entity.ENTITY_GENERATION) return content;

      return <G_WRAPPER_IMAGE__preview_on_layout_output image_asset={image}>{content}</G_WRAPPER_IMAGE__preview_on_layout_output>;
    }

    /*
     * HTML
     */

    let class_name_animation = 'transition-transform duration-500 ease-in-out group-hover:scale-110';

    //
    //shadow-[inset_0_0_0_2px_theme(colors.green.500)]

    return (
      <>
        <div
          {...{ onMouseEnter, onDragStart, onDragEnd, onDoubleClick }}
          onMouseLeave={() => set_is_mouse_over(false)}
          className={`relative flex-center size-full`}
        >
          <div className="relative size-full cursor-pointer  overflow-hidden group   rounded-xl ">
            {render_content_inside_wrapper(render_content())}

            {render_labels()}

            <GRID2D_CELL__placeholder
              {...{ width, height }}
              className={`absolute top-0 left-0 z-[-1] transition-opacity duration-200 
              ${is_image_loaded ? 'opacity-0' : 'opacity-100'}`}
            />

            <GRID2D_CELL_BORDER__image_select {...{ cell, is_mouse_over }} />

            {rendered_buttons_frame}
          </div>
        </div>
      </>
    );
  },
);
